import { BarChart3, CreditCard, Sun, PhoneCall, Mic, ShieldCheck, Mail } from 'lucide-react'
import type { LandingPersona } from '@/hooks/useLandingPersona'

interface Props {
  persona: LandingPersona
}

const FEATURES = [
  {
    Icon: BarChart3,
    title: 'You don’t need to become a media buyer.',
    body:
      'Connects to your Meta Ads and reads spend, ROAS, and what’s actually pulling weight. Tells you what to scale, what to pause, and why — in plain English.',
  },
  {
    Icon: CreditCard,
    title: 'You don’t need to chase the numbers.',
    body:
      'MRR, new trials, churn risk, revenue movement — sourced live from Stripe and read back the moment you ask. No exports, no spreadsheets.',
  },
  {
    Icon: Mail,
    title: 'You don’t need to live in your inbox.',
    body:
      'Connect your Google account and Jarvis triages your own Gmail — drafts replies for you to review, flags what needs you, and clears routine noise into a label. It reads your calendar for the day ahead and folds it into your briefing. Nothing sends without your say-so, and it never emails your customers.',
  },
  {
    Icon: Sun,
    title: 'You don’t need to check five dashboards.',
    body:
      'A morning briefing — phone, email, or on your dashboard. What moved overnight, what changed in your numbers, what you should know before your first client of the day.',
  },
  {
    Icon: PhoneCall,
    title: 'You don’t need to be at your desk.',
    body:
      'Your AI has its own phone number — yours and yours only. Driving, walking, between clients — pick up the phone and ask. Anywhere you can take a call is now your office.',
  },
  {
    Icon: Mic,
    title: 'You don’t need to pick one personality.',
    body:
      'Five fully licensed ElevenLabs voices — JARVIS, ULTRON, ARES, SYMBIOTE, F.R.I.D.A.Y. — each with its own tone. Switch any time. All voices included in every plan.',
  },
  {
    Icon: ShieldCheck,
    title: 'You don’t need to worry about your data.',
    body:
      'Per-tenant isolation in Azure. Keys encrypted at rest, secrets never exposed to the browser, restricted-scope API tokens. Professional security across the stack.',
  },
]

export default function LandingFeatures({ persona }: Props) {
  return (
    <section className="lp-section" id="features">
      <div className="lp-container">
        <div className="lp-section-header">
          <div className="lp-eyebrow">Seven freedoms</div>
          <h2>
            Everything that isn’t your craft.
            <br />
            <span style={{ color: persona.primary, textShadow: `0 0 40px ${persona.glow}` }}>
              Covered.
            </span>
          </h2>
        </div>

        <div className="lp-feature-grid">
          {FEATURES.map(({ Icon, title, body }) => (
            <div key={title} className="lp-card lp-feature">
              <div className="lp-feature-icon">
                <Icon size={22} strokeWidth={1.8} />
              </div>
              <h3>{title}</h3>
              <p>{body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}
